import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import ApexCharts from 'apexcharts'
import Tooltip from '../ui/Tooltip'

const BUTTON_BASE_CLASSES = 'tw:size-6 tw:flex tw:items-center tw:justify-center '
  + 'tw:bg-transparent tw:border-0 tw:cursor-pointer tw:transition-colors '
  + 'tw:hover:text-ds-primary'

const ACTION_BUTTON_CLASSES = `${BUTTON_BASE_CLASSES} tw:text-gray-500`

const SVG_PROPS = {
  'aria-hidden': true,
  'className': 'tw:size-4',
  'fill': 'none',
  'stroke': 'currentColor',
  'strokeLinecap': 'round',
  'strokeLinejoin': 'round',
  'strokeWidth': 2,
  'viewBox': '0 0 24 24',
  'xmlns': 'http://www.w3.org/2000/svg'
}

const ZOOM_IN_ICON = (
  <svg {...SVG_PROPS}>
    <circle cx="10" cy="10" r="7" />
    <line x1="15.5" x2="21" y1="15.5" y2="21" />
    <line x1="10" x2="10" y1="7" y2="13" />
    <line x1="7" x2="13" y1="10" y2="10" />
  </svg>
)

const ZOOM_OUT_ICON = (
  <svg {...SVG_PROPS}>
    <circle cx="10" cy="10" r="7" />
    <line x1="15.5" x2="21" y1="15.5" y2="21" />
    <line x1="7" x2="13" y1="10" y2="10" />
  </svg>
)

const modeButtonClasses = isActive =>
  `${BUTTON_BASE_CLASSES} ${isActive ? 'tw:text-ds-primary' : 'tw:text-gray-500'}`

// Delegate to the CSS-hidden ApexCharts toolbar — no public JS API for these actions.
const TOOLBAR_SELECTORS = {
  selection: '.apexcharts-zoom-icon',
  zoomin: '.apexcharts-zoomin-icon',
  zoomout: '.apexcharts-zoomout-icon',
  pan: '.apexcharts-pan-icon'
}

const triggerToolbarAction = (chartId, action) => {
  const chart = ApexCharts.getChartByID(chartId)
  chart?.el?.querySelector(TOOLBAR_SELECTORS[action])?.click()
}

const ChartInteractions = ({ chartId }) => {
  const [activeMode, setActiveMode] = useState('pan')

  const handleSelection = useCallback(() => {
    triggerToolbarAction(chartId, 'selection')
    setActiveMode('selection')
  }, [chartId])
  const handleZoomIn = useCallback(() => triggerToolbarAction(chartId, 'zoomin'), [chartId])
  const handleZoomOut = useCallback(() => triggerToolbarAction(chartId, 'zoomout'), [chartId])
  const handlePan = useCallback(() => {
    triggerToolbarAction(chartId, 'pan')
    setActiveMode('pan')
  }, [chartId])

  return (
    <div className="tw:flex tw:items-center tw:gap-2 tw:ml-auto">
      <span className="tw:text-sm tw:font-medium tw:text-gray-500">
        Interactions avec le graphique :
      </span>
      <Tooltip className="tw:inline-flex" content="Sélectionner une période">
        <button
          aria-label="Sélectionner une période"
          className={modeButtonClasses(activeMode === 'selection')}
          type="button"
          onClick={handleSelection}
        >
          <i className="fas fa-vector-square" />
        </button>
      </Tooltip>
      <Tooltip className="tw:inline-flex" content="Zoomer">
        <button aria-label="Zoomer" className={ACTION_BUTTON_CLASSES} type="button" onClick={handleZoomIn}>
          {ZOOM_IN_ICON}
        </button>
      </Tooltip>
      <Tooltip className="tw:inline-flex" content="Dézoomer">
        <button aria-label="Dézoomer" className={ACTION_BUTTON_CLASSES} type="button" onClick={handleZoomOut}>
          {ZOOM_OUT_ICON}
        </button>
      </Tooltip>
      <Tooltip className="tw:inline-flex" content="Navigation">
        <button
          aria-label="Navigation"
          className={modeButtonClasses(activeMode === 'pan')}
          type="button"
          onClick={handlePan}
        >
          <i className="fas fa-hand-paper" />
        </button>
      </Tooltip>
    </div>
  )
}

ChartInteractions.propTypes = {
  chartId: PropTypes.string.isRequired
}

export default ChartInteractions
